import { useState, useMemo } from "react";
import { CUSTOM_SCROLLBAR } from "@/lib/constants";
import { ContentDiv, PersonaCard, Section } from "../elements/card-group";
import { PersonaIcon } from "@/assets/svg-icons/persona-icon";

interface QumFunctionalLayoutProps {
  qumData: any[];
}

export default function QumFunctionalLayout({ qumData }: QumFunctionalLayoutProps) {
  // Selection state for hierarchical navigation
  const [selectedPersona, setSelectedPersona] = useState<any>(null);
  const [selectedOutcome, setSelectedOutcome] = useState<any>(null);
  const [selectedScenario, setSelectedScenario] = useState<any>(null);
  const [selectedStep, setSelectedStep] = useState<any>(null);

  // Parse functional data from QUM data
  const functionalData = useMemo(() => {
    if (!qumData || !Array.isArray(qumData)) return null;
    
    // Extract all personas
    const personas = qumData.map((personaObj: any, index: number) => ({
      id: index,
      persona: personaObj.persona,
      name: personaObj.persona,
      role: personaObj.persona,
      description: `Persona with ${personaObj.outcomes?.length || 0} outcomes`,
      outcomeCount: personaObj.outcomes?.length || 0,
      rawData: personaObj
    }));
    
    // Get outcomes for selected persona
    const outcomes = selectedPersona?.rawData?.outcomes?.map((outcome: any, index: number) => ({
      id: index,
      outcome: outcome.outcome,
      name: outcome.outcome,
      description: `${outcome.scenarios?.length || 0} scenarios`,
      persona: selectedPersona.persona,
      citationRefs: outcome.citationRefs,
      scenarioCount: outcome.scenarios?.length || 0,
      rawData: outcome
    })) || [];
    
    // Get scenarios for selected outcome
    const scenarios = selectedOutcome?.rawData?.scenarios?.map((scenario: any, index: number) => ({
      id: index,
      scenario: scenario.scenario,
      name: scenario.scenario,
      description: scenario.description,
      persona: selectedPersona?.persona,
      outcome: selectedOutcome?.outcome,
      steps: scenario.steps?.length || 0,
      stepCount: scenario.steps?.length || 0,
      rawData: scenario
    })) || [];
    
    // Get steps for selected scenario
    const steps = selectedScenario?.rawData?.steps?.map((step: any, index: number) => ({
      id: index,
      step: step.step,
      name: step.step,
      description: `${step.actions?.length || 0} actions`,
      persona: selectedPersona?.persona,
      scenario: selectedScenario?.scenario,
      actionCount: step.actions?.length || 0,
      rawData: step
    })) || [];
    
    // Get actions for selected step
    const actions = selectedStep?.rawData?.actions?.map((action: any, index: number) => ({
      id: index,
      action: action.action,
      name: action.action,
      description: `Citations: ${action.citationRefs?.length || 0}`,
      persona: selectedPersona?.persona,
      step: selectedStep?.step,
      citationRefs: action.citationRefs,
      rawData: action
    })) || [];

    return {
      personas,
      outcomes,
      scenarios,
      steps,
      actions,
    };
  }, [qumData, selectedPersona, selectedOutcome, selectedScenario, selectedStep]);

  // Click handlers for navigation
  const handlePersonaClick = (persona: any) => {
    setSelectedPersona(persona);
    setSelectedOutcome(null);
    setSelectedScenario(null);
    setSelectedStep(null);
  };

  const handleOutcomeClick = (outcome: any) => {
    setSelectedOutcome(outcome);
    setSelectedScenario(null);
    setSelectedStep(null);
  };

  const handleScenarioClick = (scenario: any) => {
    setSelectedScenario(scenario);
    setSelectedStep(null);
  };

  const handleStepClick = (step: any) => {
    setSelectedStep(step);
  };

  if (!functionalData?.personas || functionalData.personas.length === 0) {
    return (
      <div className="text-center text-muted py-8">
        <div className="text-lg">No functional data available</div>
        <div className="text-sm">This component doesn't contain QUM data</div>
      </div>
    );
  }

  return (
    <div className={`h-full overflow-y-auto ${CUSTOM_SCROLLBAR}`}>
      <div className="p-4 space-y-4">
        {/* Personas Section */}
        <Section title={`Personas (${functionalData.personas.length})`}>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            {functionalData.personas.map((persona: any) => (
              <PersonaCard
                key={persona.id}
                title={persona.persona}
                subtitle={`${persona.outcomeCount} outcomes`}
                description={persona.description}
                onClick={() => handlePersonaClick(persona)}
                isActive={selectedPersona?.id === persona.id}
                headerSize="text-lg"
                contentSize="text-base"
                icon={<PersonaIcon />}
              />
            ))}
          </div>
        </Section>

        {/* Tasks/Outcomes Section - only show when persona is selected */}
        {selectedPersona && functionalData.outcomes && functionalData.outcomes.length > 0 && (
          <Section title={`Tasks for ${selectedPersona.persona} (${functionalData.outcomes.length})`}>
            <div className="space-y-3">
              {functionalData.outcomes.map((outcome: any) => (
                <div key={outcome.id} className="cursor-pointer" onClick={() => handleOutcomeClick(outcome)}>
                  <ContentDiv
                    content={`${outcome.outcome}: ${outcome.scenarioCount} scenarios`}
                    isActive={selectedOutcome?.id === outcome.id}
                  />
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Scenarios Section - only show when outcome is selected */}
        {selectedOutcome && functionalData.scenarios && functionalData.scenarios.length > 0 && (
          <Section title={`Scenarios for "${selectedOutcome.outcome}" (${functionalData.scenarios.length})`}>
            <div className="space-y-3">
              {functionalData.scenarios.map((scenario: any) => (
                <div key={scenario.id} className="cursor-pointer" onClick={() => handleScenarioClick(scenario)}>
                  <ContentDiv
                    content={`${scenario.scenario}: ${scenario.stepCount} steps`}
                    isActive={selectedScenario?.id === scenario.id}
                  />
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Steps Section - only show when scenario is selected */}
        {selectedScenario && functionalData.steps && functionalData.steps.length > 0 && (
          <Section title={`Steps for "${selectedScenario.scenario}" (${functionalData.steps.length})`}>
            <div className="space-y-3">
              {functionalData.steps.map((step: any) => (
                <div key={step.id} className="cursor-pointer" onClick={() => handleStepClick(step)}>
                  <ContentDiv
                    content={`${step.step}: ${step.actionCount} actions`}
                    isActive={selectedStep?.id === step.id}
                  />
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Actions Section - only show when step is selected */}
        {selectedStep && functionalData.actions && functionalData.actions.length > 0 && (
          <Section title={`Actions for "${selectedStep.step}" (${functionalData.actions.length})`}>
            <div className="space-y-3">
              {functionalData.actions.map((action: any) => (
                <ContentDiv
                  key={action.id}
                  content={`${action.action} (${action.citationRefs?.length || 0} citations)`}
                />
              ))}
            </div>
          </Section>
        )}
      </div>
    </div>
  );
}